import { IQuoteItemDetailsForQuoteManagement } from 'types/quoteEditor.types';
import { filterPillsIncludingKeys } from 'utils/pillsFormat';
import { IpillsByReference } from 'types/orders';
import { formatShortDate } from 'utils/formatDatesAndHours';
import { resolveSpecLabel } from '../DeviceDescription';
import DescriptionItem, { ISpec } from '../DescriptionItem/DescriptionItem';

interface IDeviceDescriptionFromClientRequest {
  item: IQuoteItemDetailsForQuoteManagement;
}

/**
 * Renderiza un grid de especificaciones de la solicitud del cliente:
 * specs fijas de texto, specs dinámicas (pills o specifications según si es genérico),
 * indicadores con ícono (DEP, APPLE CARE+, IDIOMA DEL TECLADO, MPN) y comentarios.
 */
const DeviceDescriptionFromClientRequest = ({ item }: IDeviceDescriptionFromClientRequest) => {
  const {
    quantity,
    categoryName,
    subCategoryCode,
    isGenericProduct,
    pills,
    specifications,
    employeeProfile,
    customerRequestedDeliveryDate,
    mpn,
    hasDep,
    hasCare,
    keyboardLayout,
    customerNote,
    brand
  } = item;

  /**
   * Construye una spec de texto nullable: si value es undefined no se incluye,
   * si es null o vacío muestra ícono xCircle, si tiene valor muestra el texto.
   */
  const buildNullableSpec = (label: string, value: string | null | undefined): ISpec | null => {
    if (value === undefined) return null;
    return { label, value: value || null };
  };

  /** Claves internas que no deben mostrarse como especificación (ids, metadata). */
  const EXCLUDED_SPEC_KEYS = ['brandids'];

  const dynamicSpecsSource = isGenericProduct
    ? filterPillsIncludingKeys(specifications as IpillsByReference)
    : filterPillsIncludingKeys(pills as IpillsByReference);

  const dynamicSpecs: ISpec[] = Object.entries(dynamicSpecsSource)
    .filter(([key]) => !EXCLUDED_SPEC_KEYS.includes(key.toLowerCase().trim()))
    .map(([key, value]) => ({
      label: resolveSpecLabel(key),
      value
    }));

  const mpnSpec = buildNullableSpec('MPN', mpn);
  const isLaptopCategory = categoryName?.toLowerCase().includes('laptop') ?? false;
  const keyboardSpec: ISpec | null = isLaptopCategory
    ? { label: 'Idioma del teclado', value: keyboardLayout ?? null }
    : keyboardLayout
    ? { label: 'Idioma del teclado', value: keyboardLayout }
    : null;

  const laptopOnlySpecs: ISpec[] = isLaptopCategory
    ? [
        { label: 'DEP', value: hasDep },
        { label: 'Apple Care +', value: hasCare },
        ...(keyboardSpec ? [keyboardSpec] : [])
      ]
    : [];

  const specs: ISpec[] = [
    { label: 'Unidades solicitadas', value: String(quantity) },
    ...(categoryName ? [{ label: 'Categoría', value: categoryName }] : []),
    ...(brand ? [{ label: 'Marca', value: brand }] : []),
    ...(subCategoryCode ? [{ label: 'Subcategoría', value: subCategoryCode }] : []),
    ...dynamicSpecs,
    {
      label: 'Fecha solicitada',
      value: formatShortDate(customerRequestedDeliveryDate ?? undefined).onlyDate ?? 'Sin información'
    },
    { label: 'Perfil del empleado', value: employeeProfile || 'Sin información' },
    ...(mpnSpec ? [mpnSpec] : []),
    ...laptopOnlySpecs,
    { label: 'Comentarios adicionales', value: customerNote || 'Sin información' }
  ];

  return (
    <div className="deviceDescription deviceDescriptionFromClientRequest">
      {specs.map(spec => (
        <DescriptionItem key={spec.label} spec={spec} />
      ))}
    </div>
  );
};

export default DeviceDescriptionFromClientRequest;
